
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SignUpFormFields } from './SignUpFormFields';
import { Capacitor } from '@capacitor/core';
import type { SubscriptionType } from '@/types/subscription';

interface SignUpFormProps {
  onSubmit: (formData: {
    email: string;
    password: string;
    username: string;
    subscriptionType: SubscriptionType;
  }) => Promise<void>;
  isProcessing: boolean;
  showSuccessMessage: boolean;
  onSubscriptionChange?: (subscriptionType: SubscriptionType) => void;
  subscriptionType?: SubscriptionType;
}

export const SignUpForm = ({ 
  onSubmit, 
  isProcessing, 
  showSuccessMessage,
  onSubscriptionChange,
  subscriptionType: externalSubscriptionType
}: SignUpFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType>(externalSubscriptionType || 'select');
  const isNative = Capacitor.isNativePlatform();

  const handleSubscriptionChange = (value: SubscriptionType) => {
    setSubscriptionType(value);
    if (onSubscriptionChange) {
      onSubscriptionChange(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      email,
      password,
      username,
      subscriptionType,
    });
  };

  return (
    <Card className="p-4 sm:p-6 w-full bg-background">
      <form onSubmit={handleSubmit} className="space-y-4">
        <SignUpFormFields
          username={username}
          email={email}
          password={password}
          subscriptionType={externalSubscriptionType || subscriptionType}
          onUsernameChange={setUsername}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onSubscriptionTypeChange={handleSubscriptionChange}
        />

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isProcessing || (externalSubscriptionType || subscriptionType) === 'select'}
        >
          {isProcessing ? "Processing..." : "Continue"}
        </Button>

        {showSuccessMessage && (
          <p className="text-sm text-center text-green-600 font-medium mt-2">
            A verification link has been sent to your email. Please check your inbox.
          </p>
        )}
      </form>
    </Card>
  );
};
